import { memo, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone-latest';

import { stringifyError } from 'shared/functions';

import type { ReactElement } from 'react';
import type { DropzoneOptions, FileRejection } from 'react-dropzone-latest';

import DropzoneErrors from './DropzoneErrors';
import AcceptedFileItem, { FileWithContent } from './AcceptedFileItem';
import { getFileContentAsBase64String } from './functions';

import './Dropzone.css';

export enum dropzoneContentType {
  file = 'file',
  base64String = 'base64String',
}

export interface DropzoneProps extends DropzoneOptions {
  disabled?: boolean;
  valueByDefault?: File[];
  label?: ReactElement | string;
  error?: ReactElement | string;
  contentType?: dropzoneContentType;
  description?: ReactElement | string;
  onAcceptFiles: <CT>(files: FileWithContent<CT>[]) => void;
}

const Dropzone = ({
  label,
  error,
  description,
  disabled,
  valueByDefault,
  contentType = dropzoneContentType.base64String,
  onAcceptFiles,
  ...rest
}: DropzoneProps) => {
  const [acceptedFiles, setAcceptedFiles] = useState<FileWithContent<string>[]>(valueByDefault);
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>();

  const onDrop = useCallback(
    async (acceptedFilesFromDz: File[], fileRejections: FileRejection[]) => {
      setRejectedFiles(fileRejections);

      if (contentType === dropzoneContentType.file) {
        if (typeof onAcceptFiles === 'function') {
          onAcceptFiles(acceptedFilesFromDz);
        }
        setAcceptedFiles(acceptedFilesFromDz);
        return Promise.resolve(acceptedFilesFromDz);
      }

      const promises = acceptedFilesFromDz.map(getFileContentAsBase64String);

      const results = await Promise.allSettled(promises);

      const files: FileWithContent<string>[] = [];
      const rejections: FileRejection[] = [];

      const resultsByStatus = results.reduce(
        (acc, result, index) => {
          const file: FileWithContent<string> = acceptedFilesFromDz[index];

          if (result.status === 'rejected') {
            const rejection: FileRejection = {
              file,
              errors: [result.reason],
            };

            acc.rejections.push(rejection);

            return acc;
          }

          file.content = result.value;
          acc.files.push(file);

          return acc;
        },
        {
          files,
          rejections,
        },
      );

      if (resultsByStatus.rejections.length) {
        const allRejections = [...fileRejections, ...resultsByStatus.rejections];
        setRejectedFiles(allRejections);
      }

      setAcceptedFiles(resultsByStatus.files);
      if (typeof onAcceptFiles === 'function') {
        onAcceptFiles(resultsByStatus.files);
      }
    },
    [contentType, onAcceptFiles],
  );

  const dropzone = useDropzone({
    ...rest,
    disabled,
    multiple: rest.maxFiles > 1,
    onDrop,
  });

  const onDeleteFile = useCallback(
    (file: File) => {
      let nextFiles = acceptedFiles.filter((accepedFile) => accepedFile.name !== file.name);

      nextFiles = nextFiles.length ? nextFiles : undefined;

      setAcceptedFiles(nextFiles);

      if (typeof onAcceptFiles === 'function') {
        onAcceptFiles(nextFiles);
      }
    },
    [acceptedFiles, setAcceptedFiles, onAcceptFiles],
  );

  const onClearAll = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      const empty = undefined;
      setAcceptedFiles(empty);
      if (typeof onAcceptFiles === 'function') {
        onAcceptFiles(empty);
      }
    },
    [onAcceptFiles],
  );

  const dropzoneInner = acceptedFiles?.length ? (
    <div className="dropzone-file-inner">
      <ul className="dropzone-file-items">
        {acceptedFiles.map((file) => (
          <AcceptedFileItem file={file} key={file.name} disabled={disabled} onDeleteFile={onDeleteFile} />
        ))}
      </ul>
      <div className="dropzone-buttons">
        {!disabled && rest.maxFiles > 1 && (
          <button
            type="button"
            className="btn btn-sm btn-box-tool no-padding text-muted"
            onClick={onClearAll}
            title={translate('Clear All')}
          >
            {translate('Clear All')}
          </button>
        )}
      </div>
    </div>
  ) : (
    <div role="button" className="dropzone">
      <div>
        <i className="lnr lnr-upload fa-2x" />
      </div>
      {label}
    </div>
  );

  const errorAsString = error ? (typeof error === 'object' ? stringifyError(error) : translate(error)) : null;

  const errorElement = errorAsString ? <div className="text-danger small">{errorAsString}</div> : null;

  return (
    <div className="margin-bottom">
      <div className="dropzone-wrapper" {...dropzone.getRootProps()}>
        <input {...dropzone.getInputProps()} data-test-id="dropdown-input" />
        {dropzoneInner}
      </div>

      {description ? <div className="small text-muted">{description}</div> : null}

      <DropzoneErrors rejectedFiles={rejectedFiles} />

      {errorElement}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(Dropzone);
